// The cars team signs in here.
//
// ONE box for "who you are", because both shapes are real: the shared desk
// login is a username (capcars, beside capadmin and capsuperadmin), while
// individual staff are added by phone the way salespeople are. The backend
// matches whatever is typed against username, phone and email, so nobody has
// to remember which kind of account they were given.
//
// The superadmin creates them under Cars → Cars Team.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Input, Button, Alert } from '../common/index.js';
import { carsTeamAuth } from '../../api/carsApi';
import { errText } from '../cars/controls.jsx';
import { ROUTES } from '../../constants/app.constants';

const CarsLoginForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ login: '', password: '' });
  const [errors, setErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    setAuthError('');
  };

  const validate = () => {
    const next = {};
    if (!form.login.trim()) next.login = 'Username or phone number is required';
    if (!form.password.trim()) next.password = 'Password is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setAuthError('');
    try {
      await carsTeamAuth.login({ login: form.login.trim(), password: form.password });
      navigate(ROUTES.CARS_WORK);
    } catch (err) {
      // The backend answers 401 with the serializer's own wording, which is
      // deliberately vague about WHICH half was wrong.
      setAuthError(errText(err, 'Could not sign in. Check the username and password.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
      {authError && <Alert type="error" message={authError} />}

      <Input
        label="Username or phone"
        type="text"
        name="login"
        placeholder="capcars"
        value={form.login}
        onChange={handleChange}
        error={errors.login}
        required
        disabled={loading}
      />

      <div className="w-full">
        <label htmlFor="cars-password" className="block text-sm font-semibold text-slate-700 mb-2">
          Password <span className="text-rose-500">*</span>
        </label>
        <div className="relative">
          <input
            id="cars-password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder="Enter your password"
            value={form.password}
            onChange={handleChange}
            disabled={loading}
            autoComplete="new-password"
            className={`w-full px-4 py-3 pr-12 border rounded-xl text-slate-800 text-sm placeholder-slate-400 transition-all duration-200 focus:outline-none focus:ring-2 ${
              errors.password
                ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
                : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
            } ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            disabled={loading}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
          >
            {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
          </button>
        </div>
        {errors.password && <p className="text-rose-500 text-xs mt-1.5 font-medium">{errors.password}</p>}
      </div>

      <Button type="submit" variant="primary" size="lg" loading={loading} className="w-full">
        Sign In
      </Button>

      <p className="text-center text-xs text-slate-400">
        For the car team. Ask the superadmin to create your login under Cars → Cars Team.
      </p>
    </form>
  );
};

export default CarsLoginForm;
